import SJButton from '@/components/SJButton'
import SJFormItem from '@/components/SJFormItem'
import getIdCardInfo from '@/utils/idNumberValidate'
import { useSetState } from 'ahooks'
import dayjs from 'dayjs'
import { DatePicker, Input, Picker } from 'antd-mobile'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SJSpace from '@/components/SJSpace'
import accredit from '@/stores/accredit'
import './index.less'

const now = new Date()
const typeColumns = [
  [
    { label: '居民身份证', value: '0' },
    { label: '护照', value: '1' },
  ],
]
enum typeEnum {
  idCard,
  passport,
}

interface State {
  applicant_name: string
  applicant_name_focus: boolean
  identification_type: number
  identification_num: string
  identification_num_focus: boolean
  range: string[]
  birthday: string
  birthdayDate?: Date | null
  nameError: boolean
  nameErrorText: string
  idNumberError: boolean
  birthdayError: boolean
}
const RegistrationStep1 = () => {
  document.title = '评测报名'
  const navigate = useNavigate()
  const [state, setState] = useSetState<State>({
    applicant_name: '', // Y 评测申请人姓名
    applicant_name_focus: false,
    identification_type: typeEnum.idCard, // Y 评测申请人证件类型
    identification_num: '',
    identification_num_focus: false,
    range: ['居民身份证', '护照'],
    birthday: '',
    birthdayDate: null,
    nameError: false,
    nameErrorText: '',
    idNumberError: false,
    birthdayError: false,
  })
  const [value, setValue] = useState<(string | null)[]>(['0'])
  const [visible, setVisible] = useState(false)
  const identificationTypeName = useMemo(() => state.range[Number(value)], [state.range, value])

  useEffect(() => {
    if (accredit.applicant_result) {
      setState({
        applicant_name: accredit.applicant_result.applicant_name || '',
        identification_num: accredit.applicant_result.identification_num || '',
        birthday: accredit.applicant_result.birthday || '',
        birthdayDate: accredit.applicant_result.birthdayDate || null,
      })
      setValue(
        accredit.applicant_result.identification_type
          ? accredit.applicant_result.identification_type === typeColumns[0][0].label
            ? ['0']
            : ['1']
          : ['0'],
      )
    }
  }, [])

  const labelRenderer = useCallback((type: string, data: number) => {
    switch (type) {
      case 'year':
        return data + '年'
      case 'month':
        return data + '月'
      case 'day':
        return data + '日'
      case 'hour':
        return data + '时'
      case 'minute':
        return data + '分'
      case 'second':
        return data + '秒'
      default:
        return data
    }
  }, [])

  const checkName = () => {
    // eslint-disable-next-line
    const reg = /^[A-Za-z\u4e00-\u9fa5\s·\-]{1,12}$/ // 中英文，空格， · ，-
    if (state.applicant_name === '') {
      setState({ nameError: true, nameErrorText: '用户名不能为空' })
      return false
    } else if (!reg.test(state.applicant_name)) {
      setState({ nameError: true, nameErrorText: '用户名为1-12个字符，只支持中英文' })
      return false
    } else {
      setState({ nameError: false, nameErrorText: '' })
      return true
    }
  }

  const checkNumber = () => {
    if (!state.identification_num) {
      setState({ idNumberError: true })
      return false
    } else if (
      identificationTypeName === '居民身份证' &&
      !getIdCardInfo(state.identification_num).isTrue
    ) {
      setState({ idNumberError: true })
      return false
    } else if (identificationTypeName === '护照' && !state.birthday) {
      setState({ birthdayError: true })
      setState({ idNumberError: false })
      return false
    } else {
      setState({ idNumberError: false })
      setState({ birthdayError: false })
      return true
    }
  }

  const handleNext = () => {
    if (checkName() && checkNumber()) {
      const states = {
        applicant_name: state.applicant_name,
        identification_type: identificationTypeName,
        identification_num: state.identification_num,
        birthday: state.birthday,
        birthdayDate: state.birthdayDate,
      }
      accredit.applicant_result = states
      navigate('/registration/step2', {
        state: states,
      })
    }
  }
  const onChangeName = (val: string) => {
    setState({ applicant_name: val.trim() })
  }
  const onChangeDate = (val: Date) => {
    setState({
      birthdayDate: val,
      birthday: dayjs(val).format('YYYY-MM-DD'),
      birthdayError: false,
    })
  }
  const onChangeNumber = (val: string) => {
    setState({ identification_num: val.trim() })
  }
  return (
    <div className="page registration-step1">
      <div className="main">
        <SJSpace height={40} />
        <div className="title">填写基本信息</div>
        <SJFormItem
          title="姓名"
          titleWidth="75px"
          focus={state.applicant_name_focus}
          status={state.nameError ? 'error' : ''}
          statusText={state.nameErrorText}
        >
          <Input
            placeholder="请输入考生姓名"
            onChange={onChangeName}
            onFocus={() => setState({ applicant_name_focus: true })}
            onBlur={() => setState({ applicant_name_focus: false })}
            value={state.applicant_name}
          />
        </SJFormItem>
        <SJSpace height={10} />
        <Picker columns={typeColumns} value={value} onConfirm={setValue}>
          {(items, { open }) => {
            return (
              <SJFormItem title="证件类型" titleWidth="75px" arrow="drop" onClick={open}>
                <div className="picker-content">{identificationTypeName || '请选择证件类型'}</div>
              </SJFormItem>
            )
          }}
        </Picker>
        <SJSpace height={10} />
        <SJFormItem
          title="证件号码"
          titleWidth="75px"
          focus={state.identification_num_focus}
          status={state.idNumberError ? 'error' : ''}
          statusText="证件号码有误，请重新填写"
        >
          <Input
            placeholder="请输入证件号码"
            onChange={onChangeNumber}
            onFocus={() => setState({ identification_num_focus: true })}
            onBlur={() => setState({ identification_num_focus: false })}
            value={state.identification_num}
          />
        </SJFormItem>
        <SJSpace height={10} />
        {identificationTypeName === '护照' && (
          <SJFormItem
            title="出生日期"
            titleWidth="75px"
            // arrow='drop'
            status={state.birthdayError ? 'error' : ''}
            statusText="请选择出生日期"
          >
            <div onClick={() => setVisible(true)}>
              <DatePicker
                title="时间选择"
                visible={visible}
                onClose={() => {
                  setVisible(false)
                }}
                value={state.birthdayDate}
                // defaultValue={now}
                max={now}
                onConfirm={(val) => {
                  onChangeDate(val)
                }}
                renderLabel={labelRenderer}
              >
                {(value) =>
                  value ? (
                    <div className="picker-content">{dayjs(value).format('YYYY-MM-DD')}</div>
                  ) : (
                    <div className="picker-content placeholderClass">请选择出生日期</div>
                  )
                }
              </DatePicker>
            </div>
          </SJFormItem>
        )}
      </div>
      <SJButton type="primary" size="large" onClick={handleNext}>
        下一步
      </SJButton>
      <SJSpace height={40} />
    </div>
  )
}

export default RegistrationStep1
